import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";

import {
  ClientProgramStatus,
  type ClientTrainingProgram,
  type ClientTrainingProgramDetails,
} from "@fitness-app/data-models/entities/TrainingProgram";

import { supabase } from "~/store/initializeStore";

type ScheduledProgramWithDetails = ClientTrainingProgram & { details: ClientTrainingProgramDetails[] };

/**
 * TRE-1826 — plany ZAPLANOWANE nachodzące na oglądany miesiąc, razem z rozpiską.
 *
 * Store `traineeProgram.scheduledPrograms` trzyma same nagłówki planów (bez `workouts`),
 * więc do narysowania treningów w kalendarzu nie wystarcza.
 *
 * W przeciwieństwie do apki klientki nie ma tu ani okna
 * `TRAINING_PROGRAM_PREVIEW_WINDOW_DAYS`, ani filtra `hiddenFromClient` — to są reguły
 * widoczności DLA KLIENTKI. Trenerka planuje z wyprzedzeniem i musi widzieć również te
 * plany, które sama przed klientką schowała.
 */
export const useScheduledProgramsForRange = (traineeId: string | undefined, range: { start: Dayjs; end: Dayjs }) => {
  const rangeStart = range.start.format("YYYY-MM-DD");
  const rangeEnd = range.end.format("YYYY-MM-DD");

  const { data, isLoading } = useQuery(["scheduledProgramsForRange", traineeId, rangeStart, rangeEnd], {
    enabled: Boolean(traineeId),
    queryFn: async () => {
      if (!traineeId) {
        return [];
      }

      // Nachodzenie na zakres, nie zawieranie się w nim — plan dłuższy niż miesiąc ma
      // wypełnić także miesiące w swoim środku.
      const { data, error } = await supabase
        .from("client_training_program")
        .select("*, details:client_training_program_details (*)")
        .eq("traineeId", traineeId)
        .eq("status", ClientProgramStatus.Scheduled)
        .lte("startDate", rangeEnd)
        .gte("endDate", rangeStart)
        .order("startDate", { ascending: true })
        .overrideTypes<ScheduledProgramWithDetails[]>();

      if (error) {
        return [];
      }

      return (data ?? []).map((program) => ({
        program: program as ClientTrainingProgram,
        details: program.details?.[0] ?? null,
      }));
    },
  });

  return { scheduledPrograms: data ?? [], isLoading };
};
